package domain.garbage

import engine.model.Board
import kotlin.random.Random

/**
 * Manages incoming and board garbage for story mode.
 *
 * Responsibilities:
 * - Spawning blocked rows onto the board (Tetris Friends hole rules).
 * - Tracking **pending** incoming garbage (attacks not yet written to board).
 * - **Fireback cancellation**: when the player clears lines while pending
 *   garbage is queued, clears cancel pending lines before reaching the board.
 * - Hole column drift on each [registerLineClear] call.
 *
 * The [pendingLines] value is read by the renderer for the pending indicator bar.
 */
class StoryGarbageManager(
    private val cols: Int,
    private val columnShiftInterval: Int = 4,
    private val garbageBlockId: Int = -99,
) {
    // ── Pending incoming garbage (not yet on board) ───────────────────────────

    /** Lines queued to arrive; cancelled by player clears before hitting board. */
    var pendingLines: MutableList<Int> = mutableListOf()
        private set

    private var holeColumn: Int = Random.nextInt(cols)
    private var linesClearedSinceShift: Int = 0

    // ── Spawn ─────────────────────────────────────────────────────────────────

    /** Writes [lines] garbage rows to the bottom of [board] immediately. */
    fun spawnGarbage(board: Board) {
        if (pendingLines.isEmpty()) return
        val totalRows = board.rows

        pendingLines.forEach { lines ->
            for (r in 0 until totalRows - lines) {
                for (c in 0 until cols) {
                    board[r, c] = board[r + lines, c]
                }
            }
            for (rowOffset in 0 until lines) {
                val row = totalRows - lines + rowOffset
                val hole = computeHoleForRow(rowOffset)
                for (c in 0 until cols) {
                    board[row, c] = if (c == hole) 0 else garbageBlockId
                }
            }
        }
    }

    // ── Pending queue ─────────────────────────────────────────────────────────

    /** Adds [lines] to the pending incoming queue. */
    fun queueGarbage(lines: Int) {
        if (lines > 0) pendingLines.add(lines)
    }

    /**
     * Flushes all pending lines onto [board].
     * Call at piece-lock time after cancellation has been applied.
     */
    fun flushPendingToBoard(board: Board) {
        if (pendingLines.isEmpty()) return
        spawnGarbage(board)
        pendingLines.clear()
    }

    /**
     * Cancels [amount] pending lines (fireback).
     * Returns the number of lines actually cancelled (capped at [pendingLines]).
     */
    fun cancelPending(amount: Int): Int {
        val cancelled = minOf(amount, pendingLines.size)
        pendingLines = pendingLines.mapIndexed { index, garbage ->
            if (index == 0)
                garbage - cancelled
            else garbage
        }.toMutableList().also { pending -> pending.removeAll { it < 0 } }
        return cancelled
    }

    /** True when [pendingLines] > 0 (UI indicator should be visible). */
    val hasPendingGarbage: Boolean get() = pendingLines.isNotEmpty()

    // ── Board garbage detection ───────────────────────────────────────────────

    /**
     * Returns true when the board contains at least one cell with the garbage
     * block ID (-99).  Used by [CardEffect.RequireGarbageClear] to gate level-up.
     */
    fun hasBoardGarbage(board: Board): Boolean {
        for (r in 0 until board.rows) {
            for (c in 0 until cols) {
                if (board[r, c] == garbageBlockId) return true
            }
        }
        return false
    }

    /**
     * Counts rows that contain at least one garbage block.
     * Used for the freeze top-out wipe count and diagnostics.
     */
    fun countGarbageRows(board: Board): Int =
        (0 until board.rows).count { r ->
            (0 until cols).any { c -> board[r, c] == garbageBlockId }
        }

    // ── Line clear tracking (hole drift) ─────────────────────────────────────

    /**
     * Must be called after every line clear.
     * After [columnShiftInterval] clears the hole shifts by one column.
     */
    fun registerLineClear(linesCleared: Int) {
        if (linesCleared <= 0) return
        linesClearedSinceShift += linesCleared
        while (linesClearedSinceShift >= columnShiftInterval) {
            linesClearedSinceShift -= columnShiftInterval
            shiftHoleColumn()
        }
    }

    fun reset() {
        holeColumn = Random.nextInt(cols)
        linesClearedSinceShift = 0
        pendingLines.clear()
    }

    // ── Private ───────────────────────────────────────────────────────────────

    private fun computeHoleForRow(rowOffset: Int): Int =
        (holeColumn + rowOffset / 2).rem(cols).let { if (it < 0) it + cols else it }

    private fun shiftHoleColumn() {
        val direction = if (Random.nextBoolean()) 1 else -1
        holeColumn = (holeColumn + direction + cols).rem(cols)
    }
}
